/**
 * The body of `/progress` (#441, #556, #648; UX_EXPERIENCE §4.8): the focused course's topics as five status columns of
 * chips, "Your semester" (one timeline row per topic), and the Selected panel with a route to that topic's evidence.
 *
 * Layout only. `GET /courses/{courseRef}/topics` decides every status, evidence state and next step; this reads those
 * fields. The count in a column is how many topics sit under that status word, never a share of anything: there is no
 * bar, ring or percentage. Status is a glyph plus the word from `statusLabel`, never colour alone, and the chips wear the
 * course tint as an identifier. The focus picks which course is shown and is never sent with the request.
 *
 * "See the evidence" opens a card holding everything a conclusion rests on (invariant 3): the evidence state, the
 * contradicting and held-for-dispute notes (`mixed_evidence` is a note, never a status), the entries, and the policy
 * versions.
 *
 * `sectionName` comes from imported course material, so it renders as text only.
 */

import { useId, useState } from 'react';
import { withMorph } from '../../app/page-morph.js';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { CourseTopicsResponse } from '@davinci/contracts';
import type { LearnerStatus } from '@davinci/domain';
import { useApi } from '../../app/api-context.js';
import { useCourseFocus } from '../../app/course-focus.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { queryKeys } from '../../api/query-keys.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { localizedReason } from '../../i18n/status-reasons.js';
import { defineMessages } from '../../i18n/messages.js';
import { STATUS_DISPLAY_ORDER, statusLabel } from '../../i18n/status-labels.js';
import { EntrySentence } from './evidence-entries.js';
import { SemesterCard, semesterSpan, topicTitle } from './evidence-timeline.js';

type Topic = CourseTopicsResponse['topics'][number];

const GLYPH: Readonly<Record<LearnerStatus, string>> = {
  not_tested: '◌',
  learning: '▲',
  can_use_with_help: '■',
  can_use_independently: '◆',
  retained: '★',
};

const messages = defineMessages({
  en: {
    chooseCourse: 'Choose a course in the top bar to see its topics.',
    loading: 'Loading topics…',
    emptyHeading: 'Nothing checked yet',
    emptyBody: 'Upload and check a homework for this course, and what DaVinci learned from it appears here.',
    emptyLink: 'Upload a homework',
    topicsInCourse: 'Topics in this course',
    unresolved: 'Some exercises could not be placed in a topic yet, so they count toward none.',
    next: 'Next:',
    seeEvidence: 'See the evidence',
    hideEvidence: 'Hide the evidence',
    evidenceNone: 'No checked answers yet.',
    evidenceLimited: 'There are not enough checked answers yet to be sure.',
    evidenceConflicting: 'The checked answers disagree with each other.',
    evidenceSufficient: 'There are enough checked answers for this label.',
    contradicting: 'Some checked answers point the other way.',
    held: 'Answers under your open dispute count for nothing until it is settled.',
    checkedAnswers: 'Checked answers',
    statusPolicy: 'Status policy',
    mapping: 'Topic mapping',
    ladder: 'Help ladder',
    postAnswer: 'After-answer help',
    noneUsed: 'none used',
    prepPolicies: 'Practice in the app',
    byStatus: 'Topics by status',
    selected: 'Selected',
    evidenceFor: (name: string) => `The evidence for ${name}`,
  },
  he: {
    chooseCourse: 'בחרו קורס בסרגל העליון כדי לראות את הנושאים שלו.',
    loading: 'טוען נושאים…',
    emptyHeading: 'עוד לא נבדק כלום',
    emptyBody: 'אחרי שמעלים ובודקים שיעורי בית בקורס הזה, מה ש-DaVinci למדה מהם מופיע כאן.',
    emptyLink: 'להעלאת שיעורי בית',
    topicsInCourse: 'הנושאים בקורס הזה',
    unresolved: 'חלק מהתרגילים עדיין לא שויכו לנושא, ולכן הם לא נספרים באף אחד.',
    next: 'הבא:',
    seeEvidence: 'להצגת הראיות',
    hideEvidence: 'להסתרת הראיות',
    evidenceNone: 'עדיין אין תשובות שנבדקו.',
    evidenceLimited: 'עדיין אין מספיק תשובות שנבדקו כדי להיות בטוחים.',
    evidenceConflicting: 'התשובות שנבדקו סותרות זו את זו.',
    evidenceSufficient: 'יש מספיק תשובות שנבדקו לתווית הזאת.',
    contradicting: 'חלק מהתשובות שנבדקו מצביעות לכיוון ההפוך.',
    held: 'תשובות שנמצאות בערעור פתוח שלך לא נחשבות עד שהוא יוכרע.',
    checkedAnswers: 'תשובות שנבדקו',
    statusPolicy: 'מדיניות המצב',
    mapping: 'שיוך לנושאים',
    ladder: 'סולם העזרה',
    postAnswer: 'עזרה אחרי התשובה',
    noneUsed: 'לא נעשה שימוש',
    prepPolicies: 'תרגול באפליקציה',
    byStatus: 'נושאים לפי מצב',
    selected: 'נבחר',
    evidenceFor: (name: string) => `הראיות לנושא ${name}`,
  },
});

type Messages = typeof messages.en;

function evidenceSentence(state: Topic['evidenceState'], t: Messages): string {
  switch (state) {
    case 'none':
      return t.evidenceNone;
    case 'limited':
      return t.evidenceLimited;
    case 'conflicting':
      return t.evidenceConflicting;
    case 'sufficient_for_label':
      return t.evidenceSufficient;
  }
}

export function CourseTopics(): React.JSX.Element {
  const api = useApi();
  const t = useMessages(messages);
  const { focused } = useCourseFocus();
  const courseRef = focused?.moodleCourseId ?? null;

  const topics = useQuery({
    queryKey: queryKeys.courseTopics(courseRef ?? ''),
    queryFn: () => api.getCourseTopics(courseRef ?? ''),
    enabled: courseRef !== null,
  });

  if (focused === null) return <p>{t.chooseCourse}</p>;

  return (
    <div className="course-topics">
      {topics.isPending ? <p aria-live="polite">{t.loading}</p> : null}
      {topics.isError ? <FailureNotice error={topics.error} onRetry={() => void topics.refetch()} /> : null}

      {topics.isSuccess ? (
        topics.data.topics.length === 0 ? (
          <EmptyProgress />
        ) : topics.data.topics.every(nothingObserved) ? (
          <>
            <EmptyProgress />
            <details className="progress-topics-details">
              <summary>{t.topicsInCourse}</summary>
              <TopicsBody data={topics.data} />
            </details>
          </>
        ) : (
          <TopicsBody data={topics.data} />
        )
      ) : null}

      {topics.isSuccess && topics.data.unresolvedExercises.length > 0 ? <p className="progress-unresolved">{t.unresolved}</p> : null}
    </div>
  );
}

function EmptyProgress(): React.JSX.Element {
  const t = useMessages(messages);
  return (
    <div className="progress-empty">
      <h2>{t.emptyHeading}</h2>
      <p>{t.emptyBody}</p>
      <Link to="/homework">{t.emptyLink}</Link>
    </div>
  );
}

/**
 * Nothing about this topic was observed, not merely concluded: no evidence state, no evidence line, and no claim held
 * under a dispute. A refuted claim under an open dispute leaves `evidenceState: 'none'` (it counts for nothing until
 * settled), but a check did happen, so "nothing checked yet" would misstate the observation (invariant-auditor, #582).
 */
function nothingObserved(topic: Topic): boolean {
  return topic.evidenceState === 'none' && topic.evidence.length === 0 && topic.heldForDisputeClaimIds.length === 0;
}

/** The topic the panel opens on: the first with a checked answer, else the first. */
function defaultTopic(topics: readonly Topic[]): Topic | undefined {
  return topics.find((topic) => topic.evidence.length > 0) ?? topics[0];
}

function TopicsBody(props: { readonly data: CourseTopicsResponse }): React.JSX.Element {
  const t = useMessages(messages);
  const { locale } = useLocale();
  const { topics } = props.data;
  const span = semesterSpan(topics);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const trailId = useId();
  const selected = topics.find((topic) => topic.topicKey === selectedKey) ?? defaultTopic(topics);

  return (
    <>
      <section className="status-columns" aria-label={t.byStatus}>
        {STATUS_DISPLAY_ORDER.map((status) => {
          const inColumn = topics.filter((topic) => topic.status === status);
          return (
            <div key={status} className="status-column">
              <div className="status-column-head">
                <span aria-hidden="true" className="status-column-shape">
                  {GLYPH[status]}
                </span>
                <h2>{statusLabel(status, locale).label}</h2>
                <span className="status-column-count">{inColumn.length}</span>
              </div>
              <div className="status-chips">
                {inColumn.map((topic) => (
                  <button
                    key={topic.topicKey}
                    type="button"
                    className="topic-chip"
                    aria-pressed={topic.topicKey === selected?.topicKey}
                    onClick={() => {
                      withMorph(() => {
                        setSelectedKey(topic.topicKey);
                        setOpen(false);
                      });
                    }}
                  >
                    <bdi dir="auto">{topicTitle(topic, locale)}</bdi>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <div className="progress-lower">
        <SemesterCard topics={topics} span={span} />
        {selected === undefined ? null : (
          <aside className="selected-panel" aria-labelledby="selected-heading">
            <p className="selected-eyebrow">{t.selected}</p>
            <h2 id="selected-heading">
              <bdi dir="auto">{topicTitle(selected, locale)}</bdi>
            </h2>
            <p className="selected-status">
              <span aria-hidden="true">{GLYPH[selected.status]}</span>
              {statusLabel(selected.status, locale).label}
            </p>
            <p className="selected-reason">{localizedReason(selected.reason, locale)}</p>
            <p className="selected-next">
              <strong>{t.next}</strong> {localizedReason(selected.nextEvidenceNeeded, locale)}
            </p>
            <button
              type="button"
              className="selected-evidence-toggle"
              aria-expanded={open}
              aria-controls={trailId}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? t.hideEvidence : t.seeEvidence}
            </button>
          </aside>
        )}
      </div>

      <div id={trailId} hidden={!open}>
        {open && selected !== undefined ? <EvidenceCard topic={selected} /> : null}
      </div>
    </>
  );
}

function EvidenceCard(props: { readonly topic: Topic }): React.JSX.Element {
  const t = useMessages(messages);
  const { locale } = useLocale();
  const { topic } = props;
  return (
    <section className="evidence-card" aria-labelledby="evidence-heading">
      <h2 id="evidence-heading">{t.evidenceFor(topicTitle(topic, locale))}</h2>
      <p>{evidenceSentence(topic.evidenceState, t)}</p>
      {topic.contradictingClaimIds.length > 0 ? <p>{t.contradicting}</p> : null}
      {topic.heldForDisputeClaimIds.length > 0 ? <p>{t.held}</p> : null}
      {topic.evidence.length > 0 ? (
        <ul aria-label={t.checkedAnswers}>
          {topic.evidence.map((entry) => (
            <li key={entry.claimId}>
              <EntrySentence entry={entry} locale={locale} />
            </li>
          ))}
        </ul>
      ) : null}
      <dl>
        <dt>{t.statusPolicy}</dt>
        <dd>{topic.policyVersions.status}</dd>
        <dt>{t.mapping}</dt>
        <dd>{topic.policyVersions.mapping}</dd>
        <dt>{t.ladder}</dt>
        <dd>{topic.policyVersions.ladder.join(', ')}</dd>
        <dt>{t.postAnswer}</dt>
        <dd>{topic.policyVersions.postAnswerLadder.length === 0 ? t.noneUsed : topic.policyVersions.postAnswerLadder.join(', ')}</dd>
        {topic.policyVersions.prep.length === 0 ? null : (
          <>
            <dt>{t.prepPolicies}</dt>
            <dd>{topic.policyVersions.prep.join(', ')}</dd>
          </>
        )}
      </dl>
    </section>
  );
}
